import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getAllProperties, propertiesToArray } from '../Functions/FetchProperties';

const FeaturedProperties = () => {
    const [featuredProperties, setFeaturedProperties] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchFeaturedProperties = async () => {
            try {
                setLoading(true);
                const propertiesData = await getAllProperties();
                
                const propertiesArray = propertiesToArray(propertiesData);
                
                // Filter properties with featured tag (checking both tag and tags fields)
                const featured = propertiesArray.filter(property => {
                    // Check the singular 'tag' field
                    if (property.tag) {
                        if (typeof property.tag === 'string') {
                            const isFeature = property.tag.toLowerCase().includes('featured');
                            if (isFeature) return true;
                        }
                    }
                    
                    // Also check the plural 'tags' field
                    if (property.tags) {
                        // If tags is a string, check if it contains 'featured'
                        if (typeof property.tags === 'string') {
                            const isFeature = property.tags.toLowerCase().includes('featured');
                            if (isFeature) return true;
                        }
                        
                        // If tags is an array, check if it includes 'featured'
                        if (Array.isArray(property.tags)) {
                            const isFeature = property.tags.some(tag => 
                                typeof tag === 'string' && tag.toLowerCase() === 'featured'
                            );
                            if (isFeature) return true;
                        }
                    }
                    
                    return false;
                });
                
                setFeaturedProperties(featured);
            } catch (error) {
                console.error("Error fetching featured properties:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchFeaturedProperties();
    }, []);

    // Helper to ensure numeric values are properly displayed
    const getNumericValue = (value, defaultValue = 0) => {
        // If value is a number or a numeric string, return the integer value
        if (value !== undefined && value !== null && value !== '') {
            const numValue = parseInt(value, 10);
            return !isNaN(numValue) ? numValue : defaultValue;
        }
        return defaultValue;
    };

    // Helper to get and format price with proper fallbacks
    const getFormattedPrice = (property) => {
        let price = 0;
        
        // Check startPrice first with more robust type handling
        if (property.startPrice !== undefined && property.startPrice !== null) {
            // Ensure we handle both number and string types correctly
            const startPrice = Number(property.startPrice);
            
            if (!isNaN(startPrice) && startPrice > 0) {
                price = startPrice;
            }
        }
        
        // If no valid startPrice, check price
        if (price === 0 && property.price !== undefined && property.price !== null) {
            const propertyPrice = Number(property.price);
            if (!isNaN(propertyPrice) && propertyPrice > 0) {
                price = propertyPrice;
            }
        }
        
        // Return a dash if no valid price found
        if (price === 0) {
            return "Price on request";
        }
        
        // Format with thousand separators
        return price.toLocaleString('en-IN');
    };

    return (
        <div>
            {/* Featured Properties Section */}
            <section className="featured-section">
                <div className="container">
                    <h2 className="section-title text-center mb-5">Featured Properties</h2>
                    
                    {loading ? (
                        <div className="text-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="row">
                            {featuredProperties.length > 0 ? (
                                featuredProperties.map((property) => (
                                    <div className="col-lg-3 col-md-6 mb-4" key={property.id}>
                                        <Link to={`/property/${property.id}`} className="text-decoration-none">
                                            <div className="property-card">
                                                <div className="property-img">
                                                    <img src={property.coverImageUrl || (property.images && property.images[0])} 
                                                        alt={property.propertyTitle || property.name} className="img-fluid" />
                                                    <div className="property-tag">Featured</div>
                                                    <div className="location-chip">
                                                        <i className="fas fa-map-marker-alt" /> {property.location}
                                                    </div>
                                                </div>
                                                <div className="property-info">
                                                    <h3>{property.propertyTitle || property.name}</h3>
                                                    <div className="amenities">
                                                        <span>
                                                            <i className="fas fa-bed" /> {getNumericValue(property.beds || property.bhkCount || property.bedrooms, 1)} Beds
                                                        </span>
                                                        <span>
                                                            <i className="fas fa-bath" /> {getNumericValue(property.baths || property.bathrooms, 1)} Baths
                                                        </span>
                                                        <span>
                                                            <i className="fas fa-users" /> {getNumericValue(property.maxOccupancy || property.capacity, 2)} Guests
                                                        </span>
                                                    </div>
                                                    <div className="price">
                                                        <span className="amount">₹{getFormattedPrice(property)}</span> Starts from
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                ))
                            ) : (
                                <div className="col-12 text-center">
                                    <p>No featured properties available at the moment.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

export default FeaturedProperties;